import React, { useState, useEffect } from "react";
import ApiServices from "../../Services/ApiServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DropdownBox from "../../Shared/DropdownBox";
import {
  faCheck,
  faTimes,
  faTrash,
  faSpinner,
  faFilter,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { toastHelper } from "../../../Helpers/toastHelper";
import MenuAction from "../../Shared/MenuAction";
import Pagination from "../../Shared/Pagination";
import Loader from "../../Shared/Loader";
import DeleteAlert from "../../Shared/DeleteAlert";
import WarningAlert from "../../Shared/WarningAlert";
import DynamicAlert from "../../Shared/DynamicAlert";
import extractErrorMessage from "../../../Helpers/APIHelper";
import SoftGrid from '../../Shared/SoftGrid/SoftGrid';
import { useLanguage } from '../../../contexts/LanguageContext';

const CourseRequest = () => {
  const { t } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showMenu, setShowMenu] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [dynamicAlertState, setDynamicAlertState] = useState({
    isOpen: false,
    resolve: null,
    title: "",
    message: "",
    confirmText: "",
    cancelText: "",
    icon: "warning",
    input: null,
    inputLabel: "",
    inputPlaceholder: "",
    inputValidator: null
  });

  const showDynamicAlert = (options) => {
    return new Promise((resolve) => {
      setDynamicAlertState({
        isOpen: true,
        resolve,
        title: options.title,
        message: options.text,
        confirmText: options.confirmButtonText || t('confirm'),
        cancelText: options.cancelButtonText || t('cancel'),
        icon: options.icon || "warning",
        input: options.input,
        inputLabel: options.inputLabel,
        inputPlaceholder: options.inputPlaceholder,
        inputValidator: options.inputValidator
      });
    });
  };

  const handleDynamicAlertConfirm = (inputValue) => {
    if (dynamicAlertState.inputValidator) {
      const validationError = dynamicAlertState.inputValidator(inputValue);
      if (validationError) {
        toastHelper.error(validationError);
        return; // Don't close if validation fails
      }
    }
    if (dynamicAlertState.resolve) {
      if (dynamicAlertState.input) {
        dynamicAlertState.resolve({ isConfirmed: true, value: inputValue });
      } else {
        dynamicAlertState.resolve({ isConfirmed: true });
      }
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleDynamicAlertCancel = () => {
    if (dynamicAlertState.resolve) {
      dynamicAlertState.resolve({ isConfirmed: false, value: undefined });
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };

  const fetchRequests = async (page = 1, status = "", search = "") => {
    setLoading(true);
    try {
      let url = `CourseRequest/getAll?PageSize=${pageSize}&PageNumber=${page}&IsActive=true`;
      if (status) {
        url += `&Status=${status}`;
      }
      if (search) {
        url += `&Word=${search}`;
      }
      const response = await ApiServices.get(url);
      if (response.check) {
        setRequests(response.data.items || []);
        setTotalPages(response.data.total_pages || 1);
        setCurrentPage(response.data.current_page || 1);
      }
    } catch (error) {
      toastHelper.error(t('courseRequestsLoadError'));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRequests(currentPage, selectedStatus, searchQuery);
  }, [currentPage, selectedStatus, searchQuery, pageSize]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    setShowMenu(showMenu === id ? null : id);
  };

  const handleConfirm = async (id) => {
    try {
      // First, show the confirmation alert
      const confirmResult = await showDynamicAlert({
        title: t('confirmRequestTitle'),
        text: t('confirmRequestMessage'),
        icon: "warning",
        confirmButtonText: t('confirmYesBtn'),
        cancelButtonText: t('cancel'),
      });

      if (confirmResult.isConfirmed) {
        // If confirmed, show the note input dialog
        const { value: note } = await showDynamicAlert({
          title: t('addNoteTitle'),
          input: "textarea",
          inputLabel: t('noteLabelOptional'),
          inputPlaceholder: t('notePlaceholder'),
          confirmButtonText: t('confirm'),
          cancelButtonText: t('cancel'),
          icon: "question"
        });

        if (note !== undefined) {
          setProcessingId(id);
          const response = await ApiServices.put(
            `CourseRequest/Confirm/${id}`,
            { note }
          );
          if (response.check) {
            toastHelper.success(t('requestConfirmSuccess'));
            fetchRequests(currentPage, selectedStatus, searchQuery);
          }
        }
      }
    } catch (error) {
      toastHelper.error(t('requestConfirmError'));
    } finally {
      setProcessingId(null);
      setShowMenu(null);
    }
  };

  const handleReject = async (id) => {
    try {
      const { value: reason } = await showDynamicAlert({
        title: t('rejectRequestTitle'),
        input: "textarea",
        inputLabel: t('rejectReasonLabel'),
        inputPlaceholder: t('rejectReasonPlaceholder'),
        confirmButtonText: t('rejectBtn'),
        cancelButtonText: t('cancel'),
        icon: "warning",
        inputValidator: (value) => {
          if (!value || value.trim() === "") {
            return t('rejectReasonRequired');
          }
          return null;
        },
      });

      if (reason) {
        setProcessingId(id);
        const response = await ApiServices.put(`CourseRequest/Reject/${id}`, {
          reason,
        });
        if (response.check) {
          toastHelper.success(t('requestRejectSuccess'));
          fetchRequests(currentPage, selectedStatus, searchQuery);
        }
      }
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(error || error?.msg || t('requestRejectError'));
      }
    } finally {
      setProcessingId(null);
      setShowMenu(null);
    }
  };

  const handleDelete = (request) => {
    setSelectedRequest(request);
    setShowDeleteAlert(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRequest) return;

    setDeleteLoading(true);
    try {
      const response = await ApiServices.delete(`CourseRequest/delete/${selectedRequest.id}`);
      if (response.check) {
        toastHelper.success(t('requestDeleteSuccess'));
        fetchRequests(currentPage, selectedStatus, searchQuery);
      }
    } catch (error) {
      toastHelper.error(t('requestDeleteError'));
    } finally {
      setDeleteLoading(false);
      setShowDeleteAlert(false);
      setSelectedRequest(null);
      setShowMenu(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      1: { text: t('statusPending'), color: "warning" },
      2: { text: t('statusRejected'), color: "danger" },
      3: { text: t('statusConfirmed'), color: "success" },
    };
    const config = statusConfig[status] || {
      text: t('statusUnknown'),
      color: "secondary",
    };
    return (
      <span className={`status-badge ${config.color}`}>{config.text}</span>
    );
  };

  if (deleteLoading) {
    return <Loader text={t('deletingRequest')} />;
  }
  if (loading) {
    return <Loader text={t('loadingRequests')} />;
  }

  return (
    <div className="course-requests">
      <div className="requests-header">
        <div className="header-left">
          <h2>{t('courseRequestsTitle')}</h2>
        </div>
        <div className="header-right">
          <div className="filter-group">
            <select
              value={selectedStatus}
              onChange={handleStatusChange}
              className="status-filter"
            >
              <option value="">{t('examAllStatuses')}</option>
              <option value="1">{t('statusPending')}</option>
              <option value="2">{t('statusRejected')}</option>
              <option value="3">{t('statusConfirmed')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="requests-table">
        <SoftGrid
          columns={[
            { key: 'student_name', label: t('studentName') },
            { key: 'email', label: t('email') },
            { key: 'phone', label: t('phone') },
            { key: 'course_name', label: t('courseLabel') },
            {
              key: 'status',
              label: t('status'),
              render: (value) => getStatusBadge(value),
            },
          ]}
          data={requests}
          actions={row => [
            {
              tooltip: t('confirm'),
              icon: <FontAwesomeIcon icon={faCheck} />,
              onClick: () => handleConfirm(row.id),
              className: 'confirm-action',
              disabled: row.status === 3 || processingId === row.id,
            },
            {
              tooltip: t('rejectBtn'),
              icon: <FontAwesomeIcon icon={faTimes} />,
              onClick: () => handleReject(row.id),
              className: 'reject-action',
              disabled: row.status === 2 || processingId === row.id,
            },
            {
              tooltip: t('delete'),
              icon: <FontAwesomeIcon icon={faTrash} />,
              onClick: () => handleDelete(row),
              className: 'delete-action',
              disabled: processingId === row.id,
            },
          ]}
          isLoading={loading}
          emptyStateMessage={t('noSubscriptionsAvailable')}
          searchPlaceholder={t('searchRequestPlaceholder')}
          isPaginated={true}
          columnResizable={true}
          pageNumber={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={(newSize) => {
            setPageSize(newPageSize => {
              if (newPageSize !== newSize) {
                setCurrentPage(1);
                return newSize;
              }
              return newPageSize;
            });
          }}
          onSearch={(term) => { setSearchQuery(term); setCurrentPage(1); }}
          isColumnDraggable={true}
          isColumnSelectable={true}
        />
      </div>

      <WarningAlert
        isOpen={dynamicAlertState.isOpen && dynamicAlertState.icon === "warning"}
        onClose={handleDynamicAlertCancel}
        onConfirm={handleDynamicAlertConfirm}
        title={dynamicAlertState.title}
        message={dynamicAlertState.message}
        confirmText={dynamicAlertState.confirmText}
        cancelText={dynamicAlertState.cancelText}
        input={dynamicAlertState.input}
        inputLabel={dynamicAlertState.inputLabel}
        inputPlaceholder={dynamicAlertState.inputPlaceholder}
      />

      <DynamicAlert
        isOpen={dynamicAlertState.isOpen && dynamicAlertState.icon === "question"}
        onClose={handleDynamicAlertCancel}
        onConfirm={handleDynamicAlertConfirm}
        title={dynamicAlertState.title}
        message={dynamicAlertState.message}
        confirmText={dynamicAlertState.confirmText}
        cancelText={dynamicAlertState.cancelText}
        confirmColor="#4a90e2"
        input={dynamicAlertState.input}
        inputLabel={dynamicAlertState.inputLabel}
        inputPlaceholder={dynamicAlertState.inputPlaceholder}
      />

      <DeleteAlert
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setSelectedRequest(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDeleteRequestTitle')}
        message={t('confirmDeleteRequestMessage')}
        itemName={selectedRequest?.student_name}
        itemDescription={`${t('coursePrefix')}${selectedRequest?.course_name}`}
        loading={deleteLoading}
      />

      <style jsx>{`
        .course-requests {
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .requests-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .header-left h2 {
          margin: 0;
          color: #2c3e50;
        }

        .filter-group {
          display: flex;
          gap: 10px;
        }

        .status-filter {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
            font-family: "Zain", sans-serif;
        }

        .requests-table {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          min-height: 400px;
          display: flex;
          flex-direction: column;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.85em;
          font-weight: 500;
        }

        .status-badge.warning {
          background: #fff3cd;
          color: #856404;
        }

        .status-badge.danger {
          background: #f8d7da;
          color: #721c24;
        }

        .status-badge.success {
          background: #d4edda;
          color: #155724;
        }

        .loading-cell,
        .no-data {
          text-align: center;
          padding: 20px;
          color: #6c757d;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 10px;
          margin-top: 20px;
        }

        .page-btn {
          padding: 8px 16px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .page-btn:hover:not(:disabled) {
          background: #f8f9fa;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-info {
          color: #6c757d;
        }

        .confirm-action {
          color: #28a745;
        }

        .reject-action {
          color: #dc3545;
        }

        .delete-action {
          color: #dc3545;
        }

        @media (max-width: 768px) {
          .requests-header {
            flex-direction: column;
            gap: 10px;
          }

          .header-right {
            width: 100%;
          }

          .filter-group {
            width: 100%;
          }

          .status-filter {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseRequest;
